// React helpers for the application feature.
import { useEffect, useRef, useState } from 'react'
import type { CommandArgs, CommandName, CommandResult } from '@/domain/types'
import { PtwError } from '@/lib/commands'
import { useCommand, useMe } from '@/lib/queries'

/**
 * Estimated server time in ms: the device clock corrected by the offset seen in the
 * last ptw_me read. Used for hints and "use current time" buttons only; every
 * signing time is set by the server.
 */
export function useServerNow(): number {
  const me = useMe()
  const [, setTick] = useState(0)
  useEffect(() => {
    const t = window.setInterval(() => setTick((x) => x + 1), 30_000)
    return () => window.clearInterval(t)
  }, [])
  const skew = me.data && me.dataUpdatedAt ? Date.parse(me.data.server_time) - me.dataUpdatedAt : 0
  return Date.now() + (Number.isFinite(skew) ? skew : 0)
}

/**
 * The same server-time estimate, read when it is called (for "use the time now"
 * buttons: a value computed at the last render can be up to 30 seconds old).
 */
export function useServerClock(): () => number {
  const me = useMe()
  const skew = me.data && me.dataUpdatedAt ? Date.parse(me.data.server_time) - me.dataUpdatedAt : 0
  return () => Date.now() + (Number.isFinite(skew) ? skew : 0)
}

/**
 * useCommand plus one safeguard: after an ambiguous (network) failure the hook keeps
 * the idempotency key so a retry of the same request cannot sign twice; if the user
 * then changes the input, the key is renewed so the server does not refuse the new
 * request as a conflicting replay (src/lib/queries.ts asks callers to do this).
 */
export function useGuardedCommand<C extends CommandName>(name: C) {
  const cmd = useCommand(name)
  const ambiguous = useRef<string | null>(null)
  const run = async (args: CommandArgs[C], expectedRowVersion?: number | null): Promise<CommandResult> => {
    const digest = JSON.stringify({ args, expectedRowVersion: expectedRowVersion ?? null })
    if (ambiguous.current !== null && ambiguous.current !== digest) cmd.resetIntent()
    try {
      const result = await cmd.run(args, expectedRowVersion)
      ambiguous.current = null
      return result
    } catch (e) {
      const err = PtwError.from(e)
      ambiguous.current = err.code === 'NETWORK' ? digest : null
      throw err
    }
  }
  return { run, error: cmd.error, isPending: cmd.isPending, reset: cmd.resetIntent }
}

export function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

export function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    /* storage unavailable: the copy lasts for this page only */
  }
}
